import { isDevEnv } from "../../../utils/analyticsEnv";
import { presenterLiveMatchCard } from "../../Leagues/lib/leaguePresenterLiveMatch";

/**
 * Dev-only lobby card. Deploy builds set NODE_ENV from ENVIRONMENT
 * (dev.cricket8.com → NODE_ENV=dev; prod → production), and Parcel inlines that at build time.
 * Promo/embed demos still use {@link SIXES_DEMO_MATCH_ID} on prod — see {@link isSixesDemoMatchId}.
 */
export const SIXES_DEMO_ENABLED = isDevEnv();

/** Hardcoded fixture id for local demo grid/sim — never calls `/sixes/reveal`. */
export const SIXES_DEMO_MATCH_ID = "14872941";
/** Presenter /leaguesdemo India v West Indies board. */
export const LEAGUE_SIXES_DEMO_MATCH_ID = "proto-ind-wi";

/** Default score-multiplier face on the design-only demo card (Sixes lobby + leagues demo). */
export const SIXES_DEMO_MULTIPLIER_PLAYER = "Shreyas Iyer";
export const LEAGUE_SIXES_DEMO_MULTIPLIER_PLAYER = "Virat Kohli";

export const SIXES_DEMO_HOME_TEAM = "Punjab Kings";
export const SIXES_DEMO_AWAY_TEAM = "Mumbai Indians";
export const LEAGUE_SIXES_DEMO_HOME_TEAM = "India";
export const LEAGUE_SIXES_DEMO_AWAY_TEAM = "West Indies";

/** PBKS vs MI demo fixture — maps carousel / multiplier names to CDN team folders. */
export const SIXES_DEMO_MULTIPLIER_PLAYER_TEAMS: Readonly<Record<string, string>> = {
    [SIXES_DEMO_MULTIPLIER_PLAYER]: SIXES_DEMO_HOME_TEAM,
    "Shashank Singh": SIXES_DEMO_HOME_TEAM,
    "Rohit Sharma": SIXES_DEMO_AWAY_TEAM,
    "Suryakumar Yadav": SIXES_DEMO_AWAY_TEAM,
    [LEAGUE_SIXES_DEMO_MULTIPLIER_PLAYER]: LEAGUE_SIXES_DEMO_HOME_TEAM,
    "Hardik Pandya": LEAGUE_SIXES_DEMO_HOME_TEAM,
    "Nicholas Pooran": LEAGUE_SIXES_DEMO_AWAY_TEAM,
    "Shai Hope": LEAGUE_SIXES_DEMO_AWAY_TEAM,
};

export function isLeagueSixesDemoMatchId(matchId: string): boolean {
    return String(matchId || "").trim() === LEAGUE_SIXES_DEMO_MATCH_ID;
}

/** True for the hardcoded demo fixture in every env (embeds/promos need local data on prod too). */
export function isSixesDemoMatchId(matchId: string): boolean {
    const id = String(matchId || "").trim();
    return id === SIXES_DEMO_MATCH_ID || id === LEAGUE_SIXES_DEMO_MATCH_ID;
}

export function sixesDemoHomeTeam(matchId: string): string {
    return isLeagueSixesDemoMatchId(matchId) ? LEAGUE_SIXES_DEMO_HOME_TEAM : SIXES_DEMO_HOME_TEAM;
}

export function sixesDemoAwayTeam(matchId: string): string {
    return isLeagueSixesDemoMatchId(matchId) ? LEAGUE_SIXES_DEMO_AWAY_TEAM : SIXES_DEMO_AWAY_TEAM;
}

export function sixesDemoMultiplierPlayer(matchId: string): string {
    return isLeagueSixesDemoMatchId(matchId) ? LEAGUE_SIXES_DEMO_MULTIPLIER_PLAYER : SIXES_DEMO_MULTIPLIER_PLAYER;
}

/** Switch-player carousel pool for design-only demo cards (India/WI vs PBKS/MI). */
export function sixesDemoMultiplierSpinPlayers(matchId: string): readonly string[] {
    if (isLeagueSixesDemoMatchId(matchId)) {
        return [
            LEAGUE_SIXES_DEMO_MULTIPLIER_PLAYER,
            "Hardik Pandya",
            "Nicholas Pooran",
            "Shai Hope",
        ];
    }
    return [
        SIXES_DEMO_MULTIPLIER_PLAYER,
        "Shashank Singh",
        "Rohit Sharma",
        "Suryakumar Yadav",
    ];
}

export function sixesDemoCompetition(matchId: string): string {
    return isLeagueSixesDemoMatchId(matchId) ? "ICC Men's T20 World Cup" : "IPL 2025";
}

/** Demo scorecard — PBKS chasing MI in a T20. League demo stays blank until Start the match. */
export const SIXES_DEMO_SCORE_LINES: readonly [string, string] = ["92/1", "189/5"];
export const SIXES_DEMO_PREMATCH_SCORE_LINES: readonly [string, string] = ["--", "--"];
export const SIXES_DEMO_BATTING_TEAM = "Punjab Kings";
export const SIXES_DEMO_STICKY_OV_LABEL = "10.4/20";

export function sixesDemoScoreLines(matchId: string, matchLive = false): readonly [string, string] {
    if (isLeagueSixesDemoMatchId(matchId)) {
        if (!matchLive) return SIXES_DEMO_PREMATCH_SCORE_LINES;
        return presenterLiveMatchCard().scoreLines;
    }
    return SIXES_DEMO_SCORE_LINES;
}

export function sixesDemoStickyOvLabel(matchId: string, matchLive = false): string | null {
    if (isLeagueSixesDemoMatchId(matchId)) {
        return matchLive ? presenterLiveMatchCard().oversLabel : null;
    }
    return SIXES_DEMO_STICKY_OV_LABEL;
}

export function sixesDemoBattingTeam(matchId: string): string {
    return isLeagueSixesDemoMatchId(matchId) ? LEAGUE_SIXES_DEMO_HOME_TEAM : SIXES_DEMO_BATTING_TEAM;
}

/** Standalone PBKS/MI lobby demo is in-play. Presenter India–WI is live only after Start the match. */
export function sixesDemoIsInPlayScorecard(matchId: string, matchLive = false): boolean {
    if (!isSixesDemoMatchId(matchId)) return false;
    if (isLeagueSixesDemoMatchId(matchId)) return matchLive;
    return true;
}
